import { TextTruncate } from "components";
import { Tooltip } from "components/common/tooltip";
import { RequestStatusRenderer } from "components/requestStatsRenderer/RequestStatusRenderer";
import { RouteNames } from "enums";
import type SortDirections from "enums/sortDirections";
import { type RootState, type AppDispatch } from "features/Application/globaltypes/redux";
import {
  type PerformanceFilter,
  formattedAverageTime,
  isUserDeleted,
  formatUserName,
} from "features/Library/Common/utils/performanceUtils";
import { GenericPerformanceList } from "features/Reporting/Content/shared/GenericPerformanceList";
import { bindAction } from "interfaces";
import { type ColumnOption } from "interfaces/columnOptions";
import moment from "moment";
import { type FC, useEffect, useCallback } from "react";
import { connect, type ConnectedProps } from "react-redux";
import dateTimeUtils from "utils/dateTimeUtils";
import { selectPeopleDetailsValue, selectPeopleDetails, setTableSort } from "../state/slices/flowPerformanceSlice";
import { type FlowPeopleDetails } from "../types/performance";

import styles from "./flowPerformancePeople.module.scss";
import { MoboLink } from "components/MoboLink/MoboLink";

export type FlowPerformancePeopleProps = {
  filter: PerformanceFilter;
  includeAccounts?: boolean;
} & PropsFromRedux;

export const FlowPerformancePeopleComponent: FC<FlowPerformancePeopleProps> = ({
  filter,
  peopleDetails,
  peopleDetailsStatus,
  setTableSort,
  includeAccounts,
}) => {
  useEffect(() => {
    return () => {
      setTableSort([null, null]);
    };
  }, [setTableSort]);

  const columnOptions: ColumnOption<FlowPeopleDetails>[] = [
    {
      name: "NAME",
      width: 5,
      isSortable: false,
      render: (entity: FlowPeopleDetails) => {
        const formattedNameDisplay = formatUserName(entity.FirstName, entity.LastName);
        if (includeAccounts || isUserDeleted(entity)) {
          return (
            <Tooltip target={<TextTruncate>{formattedNameDisplay}</TextTruncate>} content={formattedNameDisplay} />
          );
        }
        return (
          <MoboLink to={`/${RouteNames.peopleUsers}/${entity.UserId}`} className={styles.linkButton}>
            <Tooltip target={<TextTruncate>{formattedNameDisplay}</TextTruncate>} content={formattedNameDisplay} />
          </MoboLink>
        );
      },
    },
    {
      name: "EMAIL",
      width: 5,
      isSortable: false,
      render: (entity: FlowPeopleDetails) => (
        <div>
          <Tooltip target={<TextTruncate>{entity.Email}</TextTruncate>} content={entity.Email} />
          <div>&nbsp;</div>
        </div>
      ),
    },
    {
      name: "DATE STARTED",
      width: 5,
      isSortable: true,
      render: (entity: FlowPeopleDetails) => {
        const date = entity.LastStartDate ? dateTimeUtils.formatDate(entity.LastStartDate) : "-";

        return (
          <div>
            <div>{date}</div>
            <div>&nbsp;</div>
          </div>
        );
      },
    },
    {
      name: "DATE COMPLETED",
      width: 5,
      isSortable: true,
      render: (entity: FlowPeopleDetails) => {
        const date = entity.LastCompletionDate !== null ? dateTimeUtils.formatDate(entity.LastCompletionDate) : "-";

        return (
          <div>
            <div>{date}</div>
            <div>&nbsp;</div>
          </div>
        );
      },
    },
    {
      name: "COMPLETION TIME",
      width: 5,
      isSortable: true,
      render: (entity: FlowPeopleDetails) => {
        const completionTime = entity.LastCompletionTime
          ? formattedAverageTime(moment.duration(entity.LastCompletionTime).asSeconds())
          : "-";
        return (
          <div>
            <div>{completionTime}</div>
            <div>&nbsp;</div>
          </div>
        );
      },
    },
  ];

  const handleSort = useCallback(
    (columnName: string, dir: SortDirections) => {
      setTableSort([columnName, dir]);
    },
    [setTableSort],
  );

  return (
    <RequestStatusRenderer state={peopleDetailsStatus}>
      <GenericPerformanceList
        rows={peopleDetails}
        columns={columnOptions}
        filter={filter}
        title="People"
        onSort={handleSort}
      />
    </RequestStatusRenderer>
  );
};

const mapStateToProps = (state: RootState) => {
  return {
    peopleDetails: selectPeopleDetailsValue(state),
    peopleDetailsStatus: selectPeopleDetails(state),
  };
};

const mapDispatchToProps = (dispatch: AppDispatch) => ({
  setTableSort: bindAction(setTableSort, dispatch),
});

const connector = connect(mapStateToProps, mapDispatchToProps);
type PropsFromRedux = ConnectedProps<typeof connector>;

const ConnectedComponent = connector(FlowPerformancePeopleComponent);
export default ConnectedComponent;
